import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetArticles, apiGetDashboardSummary } from '../services/api';
import PageHeader from '../components/PageHeader';
import ErrorBanner from '../components/ErrorBanner';
import ArticleCover from '../components/ArticleCover';
import { rememberPhase } from '../utils/lastKnownPhase';
import { ReactComponent as ArticleIcon } from '../assets/blog/article_icon.svg';
import { CATEGORIES, getBadgeColor, formatReadTime } from './articles.utils';
import { PHASE_COLORS_MAP, PHASE_API_MAP, PHASE_KEY_FROM_LABEL } from '../constants/colors';
import './Blog.css';

// Ordre physiologique du cycle — celui du ruban, pas celui d'un tri alphabétique.
const PHASE_ORDER = [
  { key: 'REGLES', label: 'Règles' },
  { key: 'FOLLICULAIRE', label: 'Folliculaire' },
  { key: 'OVULATION', label: 'Ovulation' },
  { key: 'LUTEALE', label: 'Lutéale' },
];

const Blog = () => {
  const navigate = useNavigate();
  const { getToken, user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  // Phase choisie par la lectrice dans le ruban. Distincte de `currentPhase` : l'une
  // filtre, l'autre signale seulement où la lectrice en est aujourd'hui.
  const [activePhase, setActivePhase] = useState(null);
  // Phase du jour : c'est elle qui ordonne la page, à la place de la date de publication.
  const [currentPhase, setCurrentPhase] = useState(null);

  const loadArticles = useCallback(() => {
    setLoading(true);
    setError(null);
    apiGetArticles(getToken())
      .then((data) => setArticles(data || []))
      .catch(() => setError('Impossible de charger les articles. Vérifie ta connexion et réessaie.'))
      .finally(() => setLoading(false));
  }, [getToken]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  // Un échec ici n'empêche rien : sans phase connue, la page retombe sur l'ordre
  // chronologique et le ruban disparaît.
  useEffect(() => {
    if (!user?.id) return;
    apiGetDashboardSummary(user.id, getToken())
      .then((d) => {
        const key = PHASE_KEY_FROM_LABEL[d?.current_phase] ?? null;
        setCurrentPhase(key);
        // Profite du même appel pour rafraîchir la mémoire de phase qui permet à la
        // carte « Astuce du jour » de l'accueil de s'afficher dès le premier rendu.
        rememberPhase(key);
      })
      .catch(() => {});
  }, [user?.id, getToken]);


  // Un article sans phase déclarée vaut à tout moment du cycle (cf. docstring du
  // modèle) : il traverse donc tous les filtres de phase au lieu d'être écarté. Sans
  // ça, un conseil général disparaîtrait dès qu'une phase est sélectionnée.
  const matchesPhase = (a) =>
    !activePhase || (a.phases || []).length === 0 || a.phases.includes(activePhase);

  const filteredArticles = articles.filter(
    (a) => (!activeFilter || (a.categories || []).includes(activeFilter)) && matchesPhase(a)
  );

  // Le classement : ce qui parle de la phase en cours passe devant. Mis en sommeil dès
  // qu'un filtre de phase est actif — toute la page parle alors déjà de cette phase, et
  // la mise en avant ferait doublon avec la grille.
  const forThisPhase = !activePhase && currentPhase
    ? filteredArticles.filter((a) => (a.phases || []).includes(currentPhase))
    : [];
  const theRest = filteredArticles.filter((a) => !forThisPhase.includes(a));

  const activePhaseLabel = PHASE_ORDER.find((p) => p.key === activePhase)?.label;

  const renderCard = (article) => {
    const category = (article.categories || [])[0];
    return (
      <div key={article.id} className="blog-page__card" onClick={() => navigate(`/article/${article.id}`)} style={{ cursor: 'pointer' }}>
        <ArticleCover
          className="blog-page__card-img"
          id={article.id}
          imageUrl={article.image_url}
          titre={article.titre}
          category={category}
          glyphSize="md"
        >
          {category && (
            <span className="blog-page__card-badge" style={{ background: getBadgeColor(category) }}>
              {category}
            </span>
          )}
        </ArticleCover>
        <h4 className="blog-page__card-title">{article.titre}</h4>
        <div className="blog-page__card-meta">
          <span>⏱ {formatReadTime(article.temps_lecture)}</span>
          <button className="blog-page__card-bookmark">
            <ArticleIcon className="blog-page__card-bookmark-icon" />
          </button>
        </div>
      </div>
    );
  };


  return (
    <div className="blog-page">
      <PageHeader title="Blog" />

      {/* Point d'entrée vers la rédaction. Même condition que `EditorRoute` — le rôle
          `editor` OU la capacité posée sur un compte d'un autre rôle : tester le seul
          `can_edit_blog` laissait un compte éditeur arrivé ici sans chemin de retour
          vers son back-office. */}
      {(user?.role === 'editor' || user?.can_edit_blog) && (
        <button className="blog-page__editor-link" onClick={() => navigate('/blogs')}>
          ✏️ Espace rédaction
        </button>
      )}

      {error && <ErrorBanner message={error} onRetry={loadArticles} />}
      {loading && <p style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--color-text-secondary)' }}>Chargement…</p>}

      {!loading && !error && (
        <>
          {/* Ruban de phase : à la fois filtre et repère. Rendu inconditionnellement —
              le conditionner à `currentPhase` privait de filtre tous les comptes sans
              phase du jour (coach, éditeur, sportive sans données de cycle). */}
          <div
            className="blog-page__phases"
            role="group"
            aria-label="Filtrer les articles par phase du cycle"
          >
            {PHASE_ORDER.map(({ key, label }) => {
              const selected = key === activePhase;
              const current = key === currentPhase;
              const color = PHASE_COLORS_MAP[PHASE_API_MAP[key]];
              const classes = ['blog-page__phase'];
              if (selected) classes.push('blog-page__phase--selected');
              if (current) classes.push('blog-page__phase--current');
              return (
                <button
                  type="button"
                  key={key}
                  className={classes.join(' ')}
                  // Le filtre actif l'emporte sur le repère de phase du jour : un chip
                  // qui porte les deux se lit rempli, pas seulement coloré.
                  style={
                    selected
                      ? { background: color, borderColor: color, color: 'var(--color-white)' }
                      : current
                        ? { color, borderColor: color }
                        : undefined
                  }
                  aria-pressed={selected}
                  title={current ? `${label} — ta phase actuelle` : label}
                  onClick={() => setActivePhase(selected ? null : key)}
                >
                  <span
                    className="blog-page__phase-dot"
                    style={{ background: selected ? 'currentColor' : color }}
                  />
                  {label}
                </button>
              );
            })}
          </div>

          {forThisPhase.length > 0 && (
            <>
              <h2 className="blog-page__section-title">Pour ta phase</h2>
              <div className="blog-page__grid">
                {forThisPhase.map((article) => renderCard(article))}
              </div>
            </>
          )}


          {/* Filtres catégories */}
          <h2 className="blog-page__section-title">
            {activePhaseLabel
              ? `Phase ${activePhaseLabel.toLowerCase()}`
              : forThisPhase.length > 0 ? 'Tout le reste' : 'Tous les articles'}
          </h2>
          <div className="blog-page__filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`blog-page__filter${activeFilter === cat ? ' blog-page__filter--active' : ''}`}
                onClick={() => setActiveFilter(activeFilter === cat ? null : cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grille articles */}
          {theRest.length === 0 ? (
            // Sous un filtre, « aucun article » ne veut pas dire « blog vide » : sans
            // cette distinction, la lectrice croit la page cassée plutôt que filtrée.
            <p style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--color-text-secondary)' }}>
              {activeFilter || activePhase
                ? 'Aucun article ne correspond à ce filtre.'
                : 'Aucun article pour le moment.'}
            </p>
          ) : (
            <div className="blog-page__grid">
              {theRest.map((article) => renderCard(article))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Blog;
